// Controlled-vocabulary category registry for /guides (SEO taxonomy decision
// 2026-06-13). ONE source the index grouping, the breadcrumbs, the future
// /guides/<category> hub route, and the sitemap all read — adding a category,
// or promoting one to its own hub, is a one-file edit (mirrors the "drop a .md
// and it appears" ethos of the guides engine). Order below IS the render +
// jump-nav order: moat-first (SEO §1).

export type GuideCategory = {
  // = the `category` frontmatter value; also the URL slug + section anchor id.
  slug: string
  // Short section / jump-nav label (UX §8).
  label: string
  // One-line section description (UX §8; /content may refine the copy).
  description: string
  // Keyword-tuned hub <title>/H1 — applied only when a hub exists (Brick 2).
  hubTitle: string
  // Head term the hub targets (Brick 2 SEO).
  hubKeyword: string
  // Promoted = this category is the moat earmarked for its own indexable
  // /guides/<slug> hub. Today only tournament-formats qualifies (SEO §1/§3).
  promoted: boolean
}

// Master switch for the /guides/<category> hub pages. Brick 2 turned this on
// together with registering the static category routes (BEFORE the dynamic
// /guides/:slug) in AppRouter and shipping the hub page. When on, a promoted
// category's links/sections/crumbs resolve to its hub; when off, every category
// link falls back to the on-page #anchor so nothing points at a missing route.
// Flip OFF only if all hubs are ever retired (keep the registry in sync with
// AppRouter's route set + the sitemap's PROMOTED_HUB_SLUGS mirror).
export const HUB_ROUTES_LIVE = true

// Moat-first order (SEO §1): Tournament formats → Ratings → Running → Sports.
export const CATEGORIES: GuideCategory[] = [
  {
    slug: 'tournament-formats',
    label: 'Tournament formats',
    description: 'Americano, Mexicano and more — how each format works and when to use it.',
    hubTitle: 'Padel Tournament Formats Explained — Americano, Mexicano & More',
    hubKeyword: 'padel tournament formats',
    promoted: true,
  },
  {
    slug: 'ratings-rankings',
    label: 'Ratings & rankings',
    description: 'How player rating systems work, compared.',
    hubTitle: 'Racket Sport Rating Systems Explained — DUPR, UTR, TrueSkill',
    hubKeyword: 'racket sport rating systems',
    promoted: false,
  },
  {
    slug: 'running-tournaments',
    label: 'Running tournaments & leagues',
    description: 'Run tournaments, ladders and leagues end-to-end.',
    hubTitle: 'How to Run a Tournament or League — Formats, Draws, Ratings',
    hubKeyword: 'how to run a padel tournament',
    promoted: false,
  },
  {
    slug: 'racket-sports',
    label: 'The sports',
    description: 'Padel, pickleball and tennis — the basics and how they differ.',
    hubTitle: 'Racket Sports Explained: Padel vs Pickleball vs Tennis',
    hubKeyword: 'racket sports explained',
    promoted: false,
  },
]

const BY_SLUG = new Map(CATEGORIES.map((category) => [category.slug, category]))

export function getCategory(slug: string | undefined | null): GuideCategory | undefined {
  return slug ? BY_SLUG.get(slug) : undefined
}

// A category's hub is LIVE only when it's promoted AND the hub routes have
// shipped (HUB_ROUTES_LIVE). Drives both link targets and the index section
// treatment (a live hub becomes the canonical full listing → the index stops
// duplicating the card grid; SEO §4a).
export function isCategoryHubLive(category: GuideCategory): boolean {
  return category.promoted && HUB_ROUTES_LIVE
}

// The categories that currently have a live hub page — the single set AppRouter
// registers a static /guides/<slug> route for (before /guides/:slug) and the
// canonical source for which hubs exist. Empty when HUB_ROUTES_LIVE is off.
export function getLiveHubCategories(): GuideCategory[] {
  return CATEGORIES.filter(isCategoryHubLive)
}

// Where a category label / breadcrumb crumb / jump-nav chip points: the
// indexable hub when live, otherwise the on-page section anchor (UX §3 scaling
// rule). Never a non-existent /guides/<non-promoted> URL.
export function categoryHref(category: GuideCategory): string {
  return isCategoryHubLive(category) ? `/guides/${category.slug}` : `/guides#${category.slug}`
}
